import { toBlob } from 'html-to-image';

export type OmittedPart = 'image' | 'diagram' | 'equation';

const READY_TIMEOUT_MS = 5000;
const FONTS_TIMEOUT_MS = 3000;
const EMBED_TIMEOUT_MS = 10000;
const MAX_PIXEL_RATIO = 3;
const MAX_CANVAS_PIXELS = 8_000_000;
const PENDING_ATTRIBUTE = 'data-render-pending';
const PENDING_SELECTOR = `[${PENDING_ATTRIBUTE}]`;

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const waitForPendingRenders = (node: HTMLElement, deadline: Promise<void>) => {
  const settled = new Promise<void>((resolve) => {
    const isSettled = () => !node.querySelector(PENDING_SELECTOR);
    if (isSettled()) {
      resolve();
      return;
    }

    const observer = new MutationObserver(() => {
      if (!isSettled()) return;
      observer.disconnect();
      resolve();
    });
    observer.observe(node, {
      subtree: true,
      childList: true,
      attributeFilter: [PENDING_ATTRIBUTE],
    });
    void deadline.then(() => observer.disconnect());
  });

  return Promise.race([settled, deadline]);
};

const findUnloadedImages = async (
  node: HTMLElement,
  deadline: Promise<void>
) => {
  const images = Array.from(node.querySelectorAll('img'));
  const loadedImages = new Set<HTMLImageElement>();
  const decodes = images.map((image) => {
    image.loading = 'eager';
    return image.decode().then(
      () => loadedImages.add(image),
      () => undefined
    );
  });
  await Promise.race([Promise.all(decodes), deadline]);
  return images.filter((image) => !loadedImages.has(image));
};

const isBlankWhilePending = (
  kind: string | null
): kind is 'diagram' | 'equation' => kind === 'diagram' || kind === 'equation';

const isReplyContent = (element: Element) =>
  Boolean(element.closest('.markdown'));

const layOutWithLoadedFonts = async (node: HTMLElement) => {
  node.getBoundingClientRect();
  await Promise.race([document.fonts.ready, wait(FONTS_TIMEOUT_MS)]);
};

const fitWideTables = (node: HTMLElement) => {
  for (const table of node.querySelectorAll('table')) {
    const container = table.parentElement;
    if (!container || table.offsetWidth <= container.clientWidth) continue;
    table.style.zoom = String(container.clientWidth / table.offsetWidth);
    container.style.overflow = 'visible';
  }
};

const pixelRatioFor = (node: HTMLElement) =>
  Math.min(
    MAX_PIXEL_RATIO,
    Math.sqrt(MAX_CANVAS_PIXELS / (node.offsetWidth * node.offsetHeight))
  );

const markClippedContent = (node: HTMLElement) => {
  for (const content of node.querySelectorAll('[data-clip-content]')) {
    content.toggleAttribute(
      'data-clipped',
      content.scrollHeight > content.clientHeight
    );
  }
};

export const renderMessagePng = async (
  node: HTMLElement,
  onOmitted: (part: OmittedPart) => void
) => {
  const deadline = wait(READY_TIMEOUT_MS);
  await waitForPendingRenders(node, deadline);
  const omittedElements = new Set<HTMLElement>();

  for (const image of await findUnloadedImages(node, deadline)) {
    omittedElements.add(image);
    if (isReplyContent(image)) onOmitted('image');
  }
  for (const render of node.querySelectorAll<HTMLElement>(PENDING_SELECTOR)) {
    const kind = render.getAttribute(PENDING_ATTRIBUTE);
    if (!isBlankWhilePending(kind)) continue;
    omittedElements.add(render);
    onOmitted(kind);
  }
  for (const element of omittedElements) element.style.display = 'none';

  await layOutWithLoadedFonts(node);
  fitWideTables(node);
  markClippedContent(node);

  const png = await toBlob(node, {
    pixelRatio: pixelRatioFor(node),
    includeQueryParams: true,
    filter: (element) =>
      !(element instanceof HTMLElement && omittedElements.has(element)),
    fetchRequestInit: {
      referrerPolicy: 'no-referrer',
      signal: AbortSignal.timeout(EMBED_TIMEOUT_MS),
    },
    onImageErrorHandler: (event) => {
      if (!(event instanceof Event)) return;
      if (!(event.target instanceof HTMLImageElement)) return;
      event.target.style.display = 'none';
      if (isReplyContent(event.target)) onOmitted('image');
    },
  });
  if (!png) throw new Error('Message image rendered empty');
  return png;
};

export const omittedPartsWarning = (parts: OmittedPart[]) => {
  const counts = new Map<OmittedPart, number>();
  for (const part of parts) counts.set(part, (counts.get(part) ?? 0) + 1);
  const phrases = Array.from(
    counts,
    ([part, count]) => `${count} ${count === 1 ? part : `${part}s`}`
  );
  const list = new Intl.ListFormat('en', { type: 'conjunction' });
  return `Copied, but ${list.format(phrases)} couldn't be included`;
};
